import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import * as React from 'react';
import { cn } from '@/lib/utils';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

/**
 * Radix handles the parts that are easy to get wrong by hand: focus is trapped
 * while open, returned to the trigger on close, and the rest of the page is
 * hidden from assistive technology.
 *
 * `size="workspace"` / `"workspace-wide"` is the create/edit *workspace*: a
 * panel that takes over the app shell's main region (everything right of the
 * navigation and below the top bar) in place of the list or record that
 * opened it. The navigation stays visible and usable, so the dialog using it
 * must be `modal={false}` — a modal would make the navigation inert. The page
 * behind is hidden while the workspace is open, not merely covered, so it
 * cannot be tabbed into; its React state (search, filters, page) is untouched
 * and is back as soon as the workspace closes. On a phone this is the whole
 * screen below the top bar.
 *
 * Everything else keeps the centered popup (`size="default"`): a small action
 * — a warning, a status change, a one-field dialog — does not need to take
 * over the screen just because it is a dialog.
 */
export const DialogContent = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    size?: 'default' | 'workspace' | 'workspace-wide';
  }
>(function DialogContent({ className, children, size = 'default', ...props }, ref) {
  const isWorkspace = size === 'workspace' || size === 'workspace-wide';
  // The app shell's scrolling region. Absent outside the shell (login, tests),
  // where the workspace falls back to covering the viewport.
  const [region] = React.useState(() =>
    typeof document === 'undefined' ? null : document.getElementById('main-content'),
  );
  const inRegion = isWorkspace && region !== null;

  return (
    <DialogPrimitive.Portal container={inRegion ? region : undefined}>
      {isWorkspace ? null : (
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[1px]" />
      )}
      <DialogPrimitive.Content
        ref={ref}
        // A workspace sits beside the navigation, so a click there is
        // navigation, not a dismissal.
        {...(isWorkspace
          ? { onInteractOutside: (event: Event) => event.preventDefault() }
          : {})}
        className={cn(
          isWorkspace
            ? cn(
                'z-40 flex flex-col overflow-y-auto bg-surface-raised',
                inRegion ? 'absolute inset-0' : 'fixed inset-0',
              )
            : 'fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 max-h-[calc(100vh-4rem)] overflow-y-auto rounded-card border border-border-subtle bg-surface-raised shadow-xl',
          className,
        )}
        {...props}
      >
        {inRegion && region ? <HideRegionSiblings region={region} /> : null}
        {children}
        <DialogPrimitive.Close
          className="absolute right-4 top-4 rounded-md p-1 text-ink-subtle transition-colors hover:bg-surface-sunken hover:text-ink"
          aria-label="Close"
        >
          <X className="size-4" aria-hidden="true" />
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
});

/**
 * Hides everything else in the app shell's main region for as long as it is
 * mounted. It lives *inside* the dialog's content so that it exists only while
 * the dialog is open — `DialogContent` itself stays mounted when closed.
 */
function HideRegionSiblings({ region }: { region: HTMLElement }) {
  const marker = React.useRef<HTMLSpanElement | null>(null);
  React.useEffect(() => {
    const own = marker.current?.parentElement;
    const hidden: Array<[HTMLElement, string]> = [];
    for (const child of Array.from(region.children)) {
      if (!(child instanceof HTMLElement) || child === own) continue;
      hidden.push([child, child.style.display]);
      child.style.display = 'none';
    }
    region.scrollTop = 0;
    return () => {
      for (const [child, display] of hidden) child.style.display = display;
    };
  }, [region]);
  return <span ref={marker} hidden />;
}

export function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('space-y-1 px-6 pb-4 pt-6', className)} {...props} />;
}

export function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex justify-end gap-2 border-t border-border-subtle px-6 py-4', className)}
      {...props}
    />
  );
}

export const DialogTitle = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(function DialogTitle({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn('text-base font-semibold text-ink', className)}
      {...props}
    />
  );
});

export const DialogDescription = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(function DialogDescription({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cn('text-sm text-ink-muted', className)}
      {...props}
    />
  );
});
